import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ComponentProps,
  FC,
  Fragment,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import dayjs from 'dayjs';
import { uniqBy, update } from 'lodash';
import pluralize from 'pluralize';

import {
  useFinalizeBatchReconciliationActionMutation,
  useMergeBatchReconciliationActionIntoMutation,
  useRejectBatchReconciliationActionMutation,
  useRemoveBatchReconciliationActionDuplicateMutation,
} from '@admin/mutations/AdminBatchReconciliationActionsMutations';
import {
  BatchReconciliationAction,
  useBatchReconciliationActionsQuery,
} from '@admin/queries/AdminBatchReconciliationActionsQueries';

import API from '@api/API';

import { useCurrentEntertainerQuery } from '@queries/EntertainerQueries';
import {
  FeedEvent,
  useSuperAdminPendingEvents,
  useUnauthorizedEventsQuery,
} from '@queries/EventQueries';

import { useRespondToEntertainerEventRequestMutation } from '@mutations/EntertainerMutations';
import { useUpdateEventMutation } from '@mutations/EventMutations';

import { useAnalytics } from '@hooks/useAnalytics';
import { useCitiesQuery } from '@hooks/useCurrentUserLocation';
import { useIsVisible } from '@hooks/useIsVisible';

import { useNavigate } from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';
import { findFirstAlphaCharacter } from '@utilities/findFirstAlphaCharacter';
import { mapStringToParagraphs } from '@utilities/mapStringToParagraphs';
import { openExternalUrl } from '@utilities/openExternalUrl';

import Button from '@components/buttons/Button';
import IconButton from '@components/buttons/IconButton';
import EventCard from '@components/cards/EventCard';
import { DatePickerV2 } from '@components/form/DatePicker';
import { Input } from '@components/form/Input';
import { MultipleChoiceList } from '@components/form/MultipleChoiceList';
import { Select } from '@components/form/Select';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { ButtonFilter } from '@components/layout/ButtonFilter/ButtonFilter';
import Card from '@components/layout/Card/Card';
import { useDialog } from '@components/layout/Dialog/useDialog';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { ErrorBoundary } from '@components/layout/ErrorBoundary';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';
import { LetterAvatar } from '@components/layout/LetterAvatar/LetterAvatar';
import { Screen } from '@components/layout/Screen';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { useAuthGate } from '@screens/LogInScreen/AuthGateContext';
import {
  PendingEventCard,
  SeriesAdminEditEventPopup,
} from '@screens/series/SeriesScreen/SeriesAdmin';

const useQueuePendingEventsMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: () => API.post('/v1/super-admin/pending-events/queue', {}),
  });

  return { isQueueingPendingEvents: isPending, queuePendingEventsAsync: mutateAsync };
};

const QueuePendingEventsButton = () => {
  const { isQueueingPendingEvents, queuePendingEventsAsync } = useQueuePendingEventsMutation();

  return (
    <Button
      roundness="rounded"
      onClick={() => queuePendingEventsAsync()}
      disabled={isQueueingPendingEvents}
    >
      Queue Pending Events
    </Button>
  );
};

interface WrappedPendingEventCardProps extends Omit<
  ComponentProps<typeof PendingEventCard>,
  'existingEvents'
> {
  cityId: string;
}

const WrappedPendingEventCard: FC<WrappedPendingEventCardProps> = ({
  event,
  tagId,
  cityId,
  onEdit,
  onActionCallback,
  onMergeSave,
  isReviewed,
}) => {
  return (
    <PendingEventCard
      event={event}
      tagId={tagId}
      cityId={cityId}
      onEdit={onEdit}
      onActionCallback={onActionCallback}
      onMergeSave={onMergeSave}
      isReviewed={isReviewed}
    />
  );
};

interface FinalizeActionFormProps {
  action: BatchReconciliationAction;
  updateSelectedAction: (action: BatchReconciliationActionCardAction) => void;
  onComplete: () => void;
}

const FinalizeActionForm: FC<FinalizeActionFormProps> = ({
  action,
  updateSelectedAction,
  onComplete,
}) => {
  const divRef = useRef<HTMLDivElement>(null);
  const { isVisible } = useIsVisible(divRef);

  const primaryEvent = action.metadata.hydratedEvents[action.primaryEventId];
  const duplicateEvents = action.duplicateEventIds.map(
    (eventId) => action.metadata.hydratedEvents[eventId],
  );
  const actionEvents = [primaryEvent, ...duplicateEvents];

  const [approvedEventId, setApprovedEventId] = useState<string>();
  const [imageEventId, setImageEventId] = useState<string>(primaryEvent._id);
  const [titleEventId, setTitleEventId] = useState<string | undefined>(primaryEvent._id);
  const [venueName, setVenueName] = useState<string>(primaryEvent.venueName);
  const [cityId, setCityId] = useState<string>(primaryEvent.cityId);
  const [seriesIds, setSeriesIds] = useState<string[]>([]);

  // Depends on cityId (form state, above) — re-fetches as the human changes the City
  // picker below, so existingEvents/allEvents/venueOptions all stay scoped to whichever
  // city is currently selected rather than the primary event's original city.
  const { events: unfilteredExistingEvents = [] } = useUnauthorizedEventsQuery(
    {
      cityId,
      startDate: action.startDate,
      endDate: action.startDate,
      rsvpData: 'ignore',
      forceSupply: 'true',
      includeUnapprovedRecurringEvents: 'true',
    },
    isVisible,
  );

  const existingEvents = useMemo(() => {
    const actionEventIds = [action.primaryEventId, ...action.duplicateEventIds];
    return unfilteredExistingEvents.filter((event) => !actionEventIds.includes(event._id));
  }, [unfilteredExistingEvents, action.primaryEventId, action.duplicateEventIds]);

  const allEvents = useMemo(() => {
    return uniqBy([...actionEvents, ...existingEvents], '_id');
  }, [actionEvents, existingEvents]);

  const venueOptions = useMemo(() => {
    return uniqBy(
      [
        ...allEvents.map((event) => ({
          label: event.venueName ?? event.venue?.title,
          value: event.venueName ?? event.venue?.title,
        })),
        ...allEvents
          .map((event) => action.metadata.sourceLogsByEventId[event._id]?.rawExtraction?.venue)
          .filter(Boolean)
          .map((venue) => ({ label: `${venue.name} (Raw)`, value: venue.name })),
      ],
      'value',
    );
  }, [allEvents, action.metadata.sourceLogsByEventId]);

  const cityOptions = useMemo(() => {
    return uniqBy(
      action.metadata.cities.map((city) => ({ label: city.name, value: city._id })),
      'value',
    );
  }, [action.metadata.cities]);

  const entertainerOptions = useMemo(() => {
    return uniqBy(
      Object.values(action.metadata.entertainersGroupedByEventId).flatMap((eventEntertainers) =>
        eventEntertainers.map((entertainer: { _id: string; title: string }) => ({
          label: entertainer.title,
          value: entertainer._id,
        })),
      ),
      'value',
    );
  }, [action.metadata.entertainersGroupedByEventId]);

  const [claimedEntertainerIds, setClaimedEntertainerIds] = useState<string[]>(
    entertainerOptions.map((entertainer) => entertainer.value),
  );

  const seriesOptions = useMemo(() => {
    return (
      action.metadata.seriesOptions?.map((series) => ({
        label: series.title,
        value: series._id,
      })) ?? []
    );
  }, [action.metadata.seriesOptions]);

  const isTitleFromActionEvents = useMemo(
    () => actionEvents.some((event) => event._id === titleEventId),
    [actionEvents, titleEventId],
  );

  const imageEventOptions = useMemo(
    () => (isTitleFromActionEvents ? actionEvents : allEvents),
    [isTitleFromActionEvents, actionEvents, allEvents],
  );

  const { isFinalizingBatchReconciliationAction, finalizeBatchReconciliationActionAsync } =
    useFinalizeBatchReconciliationActionMutation();

  const handleFinalize = async () => {
    await finalizeBatchReconciliationActionAsync({
      actionId: action._id,
      approvedEventId,
      titleFromEventId: titleEventId,
      imageFromEventId: imageEventId,
      venueName,
      cityId,
      claimedEntertainerIds,
      seriesIds,
    });
    onComplete();
  };

  return (
    <div ref={divRef} className="mt-4">
      {imageEventOptions.length > 1 ? (
        <>
          <HorizontalScroll
            items={[
              <div className="d-flex gap-2">
                {imageEventOptions.map((event) => (
                  <div key={event._id}>
                    <div
                      style={{ position: 'relative' }}
                      onClick={() => setImageEventId(event._id)}
                    >
                      <img
                        src={event.img}
                        alt={event.title}
                        height={160}
                        style={{
                          borderRadius: 8,
                          border:
                            imageEventId === event._id ? '4px solid var(--color-green)' : 'none',
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>,
            ]}
          />
        </>
      ) : (
        <div>
          <img
            src={primaryEvent.img}
            alt={primaryEvent.title}
            height={160}
            style={{ borderRadius: 8 }}
          />
        </div>
      )}
      <div className="mt-2">
        <div style={{ fontSize: 12, fontWeight: 500 }}>Title</div>
        <MultipleChoiceList
          options={actionEvents.map((event) => ({
            label: `${event.title}${action.metadata.eventStatusesById?.[event._id] === 'approved' || unfilteredExistingEvents.includes(event._id) ? ' ✅' : ''}`,
            value: event._id,
          }))}
          selected={titleEventId ? [titleEventId] : []}
          onChange={([updatedTitleEventId]) => {
            setTitleEventId(updatedTitleEventId);
            setApprovedEventId(undefined);
          }}
        />
        {!!existingEvents.length && (
          <>
            <div className="mt-2" style={{ fontSize: 12, fontWeight: 500 }}>
              Approved Events
            </div>
            <MultipleChoiceList
              options={existingEvents.map((event) => ({
                label: event.title,
                value: event._id,
              }))}
              selected={approvedEventId ? [approvedEventId] : []}
              onChange={([updatedApprovedEventId]) => {
                setApprovedEventId(updatedApprovedEventId);
                setTitleEventId(undefined);
              }}
            />
          </>
        )}
      </div>
      <div className="mt-2">
        <div style={{ fontSize: 12, fontWeight: 500 }}>Venue Name</div>
        <MultipleChoiceList
          options={venueOptions}
          defaultSelected={[venueName]}
          onChange={([updatedVenueName]) => {
            setVenueName(updatedVenueName);
          }}
        />
      </div>
      <div className="mt-2">
        <div style={{ fontSize: 12, fontWeight: 500 }}>City</div>
        <MultipleChoiceList
          options={cityOptions}
          defaultSelected={[cityId]}
          onChange={([updatedCityId]) => {
            setCityId(updatedCityId);
          }}
        />
      </div>
      {!!entertainerOptions.length && (
        <div className="mt-2">
          <div style={{ fontSize: 12, fontWeight: 500 }}>Entertainers</div>
          <MultipleChoiceList
            isMultiSelect
            options={entertainerOptions}
            defaultSelected={claimedEntertainerIds}
            onChange={(updatedEntertainerIds) => {
              setClaimedEntertainerIds(updatedEntertainerIds);
            }}
          />
        </div>
      )}

      {!!seriesOptions.length && (
        <div className="mt-2">
          <div style={{ fontSize: 12, fontWeight: 500 }}>Series</div>
          <MultipleChoiceList
            isMultiSelect
            options={seriesOptions}
            defaultSelected={seriesIds}
            onChange={setSeriesIds}
          />
        </div>
      )}

      <div className="mt-4">
        <Button
          roundness="rounded"
          disabled={isFinalizingBatchReconciliationAction}
          onClick={handleFinalize}
        >
          Finalize
        </Button>
      </div>
    </div>
  );
};

interface RejectActionProps {
  action: BatchReconciliationAction;
  updateSelectedAction: (action: BatchReconciliationActionCardAction) => void;
  onComplete: () => void;
}

const RejectActionButton: FC<RejectActionProps> = ({ action, onComplete }) => {
  const { isRejectingBatchReconciliationAction, rejectBatchReconciliationActionAsync } =
    useRejectBatchReconciliationActionMutation();

  const handleReject = async () => {
    await rejectBatchReconciliationActionAsync({ actionId: action._id, reason: '' });
    onComplete();
  };

  return (
    <IconButton
      color="danger"
      variant="smooth"
      disabled={isRejectingBatchReconciliationAction}
      onClick={handleReject}
    >
      <FontAwesomeIcon name="times" />
    </IconButton>
  );
};

const RejectActionForm: FC<RejectActionProps> = ({ action, onComplete }) => {
  const { isRejectingBatchReconciliationAction, rejectBatchReconciliationActionAsync } =
    useRejectBatchReconciliationActionMutation();

  const handleReject = async () => {
    await rejectBatchReconciliationActionAsync({ actionId: action._id, reason: '' });
    onComplete();
  };

  return (
    <div className="mt-4">
      <Button
        roundness="rounded"
        color="danger"
        disabled={isRejectingBatchReconciliationAction}
        onClick={handleReject}
      >
        Reject
      </Button>
    </div>
  );
};

interface OverrideAndReprocessActionFormProps {
  action: BatchReconciliationAction;
  onSubmit: () => void;
}

const OverrideAndReprocessActionForm: FC<OverrideAndReprocessActionFormProps> = ({
  action,
  onSubmit,
}) => {
  const primaryEvent = action.metadata.hydratedEvents[action.primaryEventId];
  const [title, setTitle] = useState<string>(primaryEvent.title);
  const [startDate, setStartDate] = useState<string>(primaryEvent.startDate);
  const [venueName, setVenueName] = useState<string>(primaryEvent.venueName);
  const [cityId, setCityId] = useState<string>(primaryEvent.cityId);

  const { cities = [] } = useCitiesQuery();

  const { updateEventAsync } = useUpdateEventMutation({ eventId: action.primaryEventId });
  const handleOverrideAndReprocess = async () => {
    await updateEventAsync({
      title,
      startDate,
      venue: {
        title: venueName,
      },
      cityId,
    });
    onSubmit();
  };

  return (
    <div>
      <div
        className="d-grid gap-2 p-2 mt-2"
        style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 8 }}
      >
        <DatePickerV2
          value={startDate}
          onChange={(date) => setStartDate(date ? dayjs(date).format('YYYY-MM-DD') : '')}
        />
        <Input
          type="text"
          placeholder="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <Input
          type="text"
          placeholder="Venue Name"
          value={venueName}
          onChange={(e) => setVenueName(e.target.value)}
        />
        <Select
          options={cities
            .filter((city) => city.eventsAreEnabled)
            .map((city) => ({ label: city.name, value: city._id }))}
          value={cityId}
          onChange={(e) => setCityId(e.target.value)}
        />
        <div>
          <Button
            roundness="rounded"
            color="danger"
            disabled={false}
            onClick={handleOverrideAndReprocess}
          >
            Override & Reprocess
          </Button>
        </div>
      </div>
    </div>
  );
};

interface SplitActionFormProps {
  action: BatchReconciliationAction;
  updateSelectedAction: (action: BatchReconciliationActionCardAction) => void;
  onComplete: () => void;
}

const SplitActionForm: FC<SplitActionFormProps> = ({ action, onComplete }) => {
  const [selectedEventId, setSelectedEventId] = useState<string>(action.duplicateEventIds?.[0]);
  const primaryEvent = action.metadata.hydratedEvents[action.primaryEventId];
  const duplicateEvents = action.duplicateEventIds.map(
    (eventId) => action.metadata.hydratedEvents[eventId],
  );
  const actionEvents = [primaryEvent, ...duplicateEvents];

  const {
    isRemovingBatchReconciliationActionDuplicate,
    removeBatchReconciliationActionDuplicateAsync,
  } = useRemoveBatchReconciliationActionDuplicateMutation();

  const handleSplit = async () => {
    if (!selectedEventId) {
      return;
    }

    await removeBatchReconciliationActionDuplicateAsync({
      actionId: action._id,
      eventId: selectedEventId,
      reason: '',
    });
    onComplete();
  };

  return (
    <div className="mt-2">
      <div style={{ fontSize: 12, fontWeight: 500 }}>Events</div>
      <MultipleChoiceList
        options={actionEvents.map((event) => ({
          label: event.title,
          value: event._id,
          isDisabled: event._id === action.primaryEventId,
        }))}
        selected={selectedEventId ? [selectedEventId] : []}
        onChange={([updatedEventId]) => {
          setSelectedEventId(updatedEventId);
        }}
      />
      <div className="mt-4">
        <Button
          roundness="rounded"
          color="danger"
          disabled={!selectedEventId || isRemovingBatchReconciliationActionDuplicate}
          onClick={handleSplit}
        >
          Split
        </Button>
      </div>
    </div>
  );
};

interface MergeActionFormProps {
  isSelectedMergeSourceAction: boolean;
  action: BatchReconciliationAction;
  onEnterMergeMode: (actionId: string | undefined) => void;
}

const MergeActionForm: FC<MergeActionFormProps> = ({
  isSelectedMergeSourceAction,
  action,
  onEnterMergeMode,
}) => {
  if (isSelectedMergeSourceAction) {
    const handleExitMergeMode = () => {
      onEnterMergeMode(undefined);
    };

    return (
      <div className="mt-4">
        <Button
          roundness="rounded"
          variant="smooth"
          color="neutral"
          disabled={false}
          onClick={handleExitMergeMode}
        >
          Cancel Merge
        </Button>
      </div>
    );
  }

  const handleEnterMergeMode = () => {
    onEnterMergeMode(isSelectedMergeSourceAction ? undefined : action._id);
  };

  return (
    <div className="mt-4">
      <Button roundness="rounded" color="danger" disabled={false} onClick={handleEnterMergeMode}>
        Enter Merge Mode
      </Button>
    </div>
  );
};

enum BatchReconciliationActionCardAction {
  Finalize = 'finalize',
  Reject = 'reject',
  Split = 'split',
  Merge = 'merge',
  OverrideAndReprocess = 'overrideAndReprocess',
}

const BatchReconciliationActionCard = ({
  action,
  mergeSourceActionId,
  onEnterMergeMode,
}: {
  action: BatchReconciliationAction;
  mergeSourceActionId?: string;
  onEnterMergeMode: (actionId: string | undefined) => void;
}) => {
  const [didSubmit, setDidSubmit] = useState<boolean>(false);
  const primaryEvent = action.metadata.hydratedEvents[action.primaryEventId];

  const [selectedAction, setSelectedAction] = useState<BatchReconciliationActionCardAction>(
    BatchReconciliationActionCardAction.Finalize,
  );
  const actionOptions = useMemo(() => {
    return [
      {
        value: BatchReconciliationActionCardAction.Finalize,
        label: 'Finalize this action',
        isVisible: true,
      },
      {
        value: BatchReconciliationActionCardAction.Split,
        label: 'Remove an event',
        isVisible: !!action.duplicateEventIds.length,
      },
      {
        value: BatchReconciliationActionCardAction.Merge,
        label: 'Merge into another action',
        isVisible: true,
      },
      {
        value: BatchReconciliationActionCardAction.OverrideAndReprocess,
        label: 'Override & reprocess',
        isVisible: true,
      },
    ].filter((option) => option.isVisible);
  }, [action.duplicateEventIds.length]);

  const { isMergingBatchReconciliationActionInto, mergeBatchReconciliationActionIntoAsync } =
    useMergeBatchReconciliationActionIntoMutation();

  const queryClient = useQueryClient();

  const handleComplete = async () => {
    setDidSubmit(true);
    await queryClient.invalidateQueries({
      queryKey: useBatchReconciliationActionsQuery.queryKey(),
    });
  };

  const handleMergeIntoAction = async () => {
    if (!mergeSourceActionId) {
      return;
    }

    await mergeBatchReconciliationActionIntoAsync({
      actionId: mergeSourceActionId,
      targetActionId: action._id,
      reason: '',
    });

    onEnterMergeMode(undefined);

    await queryClient.invalidateQueries({
      queryKey: useBatchReconciliationActionsQuery.queryKey(),
    });
  };

  const handleAfterOverrideAndReprocessSubmit = async () => {
    await queryClient.invalidateQueries({
      queryKey: useBatchReconciliationActionsQuery.queryKey(),
    });
    setDidSubmit(true);
  };

  if (didSubmit) {
    return <></>;
  }

  return (
    <Card key={action._id}>
      <div>
        <Card.Header
          avatar={
            <>
              <img
                src={primaryEvent.img}
                alt={primaryEvent.title}
                height={60}
                style={{
                  borderRadius: 4,
                }}
              />
            </>
          }
          title={primaryEvent.title}
          tagline={[
            action.type === 'merge'
              ? 'Merge' + (action.approvedPrimary ? ' & Approve' : '')
              : 'Auto-Approve',

            action.matchType ?? '',
          ]
            .filter(Boolean)
            .join(' | ')}
          subtitle={[primaryEvent.timeRange, primaryEvent.venueName].join(' | ')}
          action={
            <RejectActionButton
              action={action}
              updateSelectedAction={setSelectedAction}
              onComplete={handleComplete}
            />
          }
        />
        <Card.Body>
          {(!mergeSourceActionId || mergeSourceActionId === action._id) && (
            <>
              <div>
                <div style={{ fontSize: 12, fontWeight: 500 }}>Action</div>
                <MultipleChoiceList
                  options={actionOptions}
                  selected={[selectedAction]}
                  onChange={([updatedAction]) => {
                    setSelectedAction(updatedAction as BatchReconciliationActionCardAction);
                  }}
                />
              </div>

              {selectedAction === BatchReconciliationActionCardAction.Finalize && (
                <FinalizeActionForm
                  action={action}
                  updateSelectedAction={setSelectedAction}
                  onComplete={handleComplete}
                />
              )}

              {selectedAction === BatchReconciliationActionCardAction.Reject && (
                <RejectActionForm
                  action={action}
                  updateSelectedAction={setSelectedAction}
                  onComplete={handleComplete}
                />
              )}

              {selectedAction === BatchReconciliationActionCardAction.Split && (
                <SplitActionForm
                  action={action}
                  updateSelectedAction={setSelectedAction}
                  onComplete={handleComplete}
                />
              )}

              {selectedAction === BatchReconciliationActionCardAction.Merge && (
                <MergeActionForm
                  isSelectedMergeSourceAction={mergeSourceActionId === action._id}
                  action={action}
                  onEnterMergeMode={onEnterMergeMode}
                />
              )}

              {selectedAction === BatchReconciliationActionCardAction.OverrideAndReprocess && (
                <OverrideAndReprocessActionForm
                  action={action}
                  onSubmit={handleAfterOverrideAndReprocessSubmit}
                />
              )}
            </>
          )}

          {mergeSourceActionId && mergeSourceActionId !== action._id && (
            <div>
              <Button
                roundness="rounded"
                color="danger"
                disabled={isMergingBatchReconciliationActionInto}
                onClick={handleMergeIntoAction}
              >
                Merge into action
              </Button>
            </div>
          )}
        </Card.Body>
      </div>
    </Card>
  );
};

const ByActionsDateGroupContent = ({
  isVisible,
  startDate,
  filter,
  mergeSourceActionId,
  updateMergeSourceActionId,
  onHideDateGroup,
}: {
  isVisible: boolean;
  startDate: string;
  filter: string;
  mergeSourceActionId: string | undefined;
  updateMergeSourceActionId: (actionId: string | undefined) => void;
  onHideDateGroup: () => void;
}) => {
  const {
    batchReconciliationActionsAreReady,
    batchReconciliationActions: filteredBatchReconciliationActions = [],
  } = useBatchReconciliationActionsQuery(
    {
      filter,
      startDate,
    },
    {
      isEnabled: isVisible,
    },
  );

  useEffect(() => {
    if (batchReconciliationActionsAreReady && !filteredBatchReconciliationActions.length) {
      onHideDateGroup();
    }
  }, [batchReconciliationActionsAreReady, filteredBatchReconciliationActions]);

  if (!batchReconciliationActionsAreReady) {
    return <div style={{ height: 1000 }}>Loading...</div>;
  }

  return (
    <div>
      {filteredBatchReconciliationActions.length ? (
        filteredBatchReconciliationActions.map((action) => (
          <BatchReconciliationActionCard
            key={action._id}
            action={action}
            mergeSourceActionId={mergeSourceActionId}
            onEnterMergeMode={updateMergeSourceActionId}
          />
        ))
      ) : (
        <EmptyState
          title="No batch reconciliation actions"
          content="No batch reconciliation actions found"
        />
      )}
    </div>
  );
};

const ByActionsDateGroup = ({
  startDate,
  filter,
  mergeSourceActionId,
  updateMergeSourceActionId,
}: {
  startDate: string;
  filter: string;
  mergeSourceActionId: string | undefined;
  updateMergeSourceActionId: (actionId: string | undefined) => void;
}) => {
  const divRef = useRef<HTMLDivElement>(null);
  const { isVisible } = useIsVisible(divRef);
  const [isHidden, setIsHidden] = useState<boolean>(false);

  if (isHidden) {
    return <></>;
  }

  return (
    <>
      <TitleToolbar text={dayjs(startDate).format('MMM D, YYYY')} size="md" />
      <div ref={divRef}>
        <ByActionsDateGroupContent
          isVisible={isVisible}
          startDate={startDate}
          filter={filter}
          mergeSourceActionId={mergeSourceActionId}
          updateMergeSourceActionId={updateMergeSourceActionId}
          onHideDateGroup={() => setIsHidden(true)}
        />
      </div>
    </>
  );
};

const ByActionsSection = ({}) => {
  const [startDate, setStartDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
  const [filter, setFilter] = useState<string>('withEntertainers');
  const {
    batchReconciliationActionsAreReady,
    batchReconciliationActionDates,
    batchReconciliationActionTotal,
  } = useBatchReconciliationActionsQuery({
    filter,
    startDate,
  });
  const [mergeSourceActionId, setMergeSourceActionId] = useState<string>();

  if (!batchReconciliationActionsAreReady) {
    return <div>Loading...</div>;
  }

  return (
    <div className="d-grid grid-cols-1-min">
      <div className="mb-2">
        <Select
          options={[
            { value: 'all', label: 'All' },
            { value: 'withEntertainers', label: 'With Entertainers' },
          ]}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>
      {batchReconciliationActionTotal ? (
        batchReconciliationActionDates?.map((date) => (
          <ByActionsDateGroup
            key={date}
            startDate={date}
            filter={filter}
            mergeSourceActionId={mergeSourceActionId}
            updateMergeSourceActionId={setMergeSourceActionId}
          />
        ))
      ) : (
        <EmptyState
          title="Uh oh!"
          content={
            filter === 'all'
              ? 'No batch reconciliation actions yet.'
              : 'No batch reconciliation actions matching this filter.'
          }
        />
      )}
    </div>
  );
};

const ByEventsSection = ({}) => {
  const { track } = useAnalytics();
  const navigate = useNavigate();

  const [editEvent, setEditEvent] = useState<{ _id: string; tagId: string }>();
  const [reviewedPendingEvents, setReviewedPendingEvents] = useState<Record<string, boolean>>({});

  const { cities = [] } = useCitiesQuery();
  const {
    pendingEvents = [],
    pendingEventsMeta = { seriesIdsByCityId: {}, cityIdsByVenueId: {} },
  } = useSuperAdminPendingEvents();
  const [cityId, setCityId] = useState<string>('');

  const { isQueueingPendingEvents, queuePendingEventsAsync } = useQueuePendingEventsMutation();

  const pendingEventCityOptions = useMemo(() => {
    return [
      { value: '', label: 'All Cities' },
      ...cities
        .filter(({ _id }) => pendingEventsMeta.seriesIdsByCityId[_id])
        .map(({ _id, name }) => ({
          value: _id,
          label: name,
        })),
    ];
  }, [cities, pendingEventsMeta.seriesIdsByCityId]);

  const filteredPendingEvents = useMemo(() => {
    if (!cityId) {
      return pendingEvents;
    }

    return pendingEvents.filter(
      (event) =>
        pendingEventsMeta.cityIdsByVenueId[event.venue._id] &&
        pendingEventsMeta.cityIdsByVenueId[event.venue._id] === cityId,
    );
  }, [cityId, pendingEvents, pendingEventsMeta]);

  return (
    <>
      <Select
        options={pendingEventCityOptions}
        value={cityId}
        onChange={(e) => setCityId(e.target.value)}
      />

      <div className="d-grid mt-4 gap-4">
        {filteredPendingEvents?.map((event) => (
          <div key={event._id}>
            <WrappedPendingEventCard
              event={event}
              cityId={pendingEventsMeta.cityIdsByVenueId[event.venue._id]}
              tagId={
                pendingEventsMeta.seriesIdsByCityId[
                  pendingEventsMeta.cityIdsByVenueId[event.venue._id]
                ]
              }
              onEdit={() =>
                setEditEvent({
                  _id: event._id,
                  tagId:
                    pendingEventsMeta.seriesIdsByCityId[
                      pendingEventsMeta.cityIdsByVenueId[event.venue._id]
                    ],
                })
              }
              onActionCallback={() =>
                setReviewedPendingEvents((prevValue) => ({
                  ...prevValue,
                  [event._id]: true,
                }))
              }
              onMergeSave={() => {}}
              isReviewed={reviewedPendingEvents[event._id]}
            />
          </div>
        ))}
      </div>

      <SeriesAdminEditEventPopup
        cityOptions={cities.map(({ _id, name }) => ({ value: _id, label: name }))}
        eventId={editEvent?._id ?? ''}
        tagId={editEvent?.tagId ?? ''}
        onApprove={() => {}}
        onClose={(didAction) => {
          if (didAction) {
            setReviewedPendingEvents((prevValue) => ({
              ...prevValue,
              [editEvent?._id ?? '']: true,
            }));
          }

          setEditEvent(undefined);
        }}
      />
    </>
  );
};

enum TabOption {
  ByEvents = 'byEvents',
  ByActions = 'byActions',
}

const TAB_OPTION_RECORD: Record<TabOption, string> = {
  [TabOption.ByActions]: 'By Actions',
  [TabOption.ByEvents]: 'By Events',
};

export const SuperAdminPendingEventsScreen = createScreenComponent(({ f7route }) => {
  const [currentTab, setCurrentTab] = useState<string>(TabOption.ByActions);

  return (
    <Screen
      name="Super Admin Pending Events Screen"
      headerProps={{
        title: 'Manage',
      }}
      hideToolbar
      disableLocationPermissionGate
    >
      <Screen.Content maxWidth={425}>
        <TitleToolbar text="Pending Events" size="md" />

        <div>
          <QueuePendingEventsButton />
        </div>

        <ButtonFilter
          options={Object.entries(TAB_OPTION_RECORD).map(([value, label]) => ({ value, label }))}
          selected={currentTab}
          onChange={setCurrentTab}
        />

        {currentTab === TabOption.ByEvents && <ByEventsSection />}
        {currentTab === TabOption.ByActions && <ByActionsSection />}
      </Screen.Content>
    </Screen>
  );
});
